// external
import React, { FC, useState, useMemo, useEffect, useCallback } from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  InputAdornment,
  TextField,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';

// internal
import COETrainingDraftModal from '../coe-training-draft-modal/coe-training-draft-modal.component';
import '../coe-training-draft-modal/coe-training-draft-modal.component.css';
import CreateTrainingDraftComponent from '../create-training-draft/create-training-draft.component';
import { Team, Group } from '../../../models/training.models';
import COESkeletonComponent from '../../general/coe-skeleton/coe-skeleton.component';
import useService from '../../../hooks/service.hook';
import TrainingLibraryService from '../../../services/training-library.service';
import useSnackbarHelper from '../../../hooks/snackbar.hook';
import EditTrainingComponent from '../edit-training/edit-training.component';
import { palette } from '../../../theme';
import COETrainingTemplateCard, {
  TrainingTemplate,
  PopoverAction,
} from '../../coe-training-template-card/coe-training-template-card.component';
import './coe-training-library.component.css';

interface COETrainingLibraryProps {
  open: boolean;
  handleClose: () => void;
  handleRefresh?: () => void;
  team: Team;
  week?: any;
  group?: Group;
  selectedDate?: any;
}

const COETrainingLibraryComponent: FC<COETrainingLibraryProps> = (props) => {
  const trainingLibraryService = useService(TrainingLibraryService);
  const { showSnackbar } = useSnackbarHelper();
  const [templates, setTemplates] = useState<TrainingTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedSport, setSelectedSport] = useState<string>('');
  const [selectedSportType, setSelectedSportType] = useState<string>('');
  const [selectedFocus, setSelectedFocus] = useState<string>('');
  const [duplicateModalOpen, setDuplicateModalOpen] = useState<boolean>(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(undefined);
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  const [editTemplateId, setEditTemplateId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<boolean>(false);

  const fetchTrainingLibrary = useCallback(async () => {
    if (!props.open) {
      setLoading(false);
      return;
    }

    const teamId = props.team?.teamId || props.team?.id;
    if (!teamId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await trainingLibraryService.getTrainingLibrary(teamId);
      const templatesData = response.data?.data || response.data || [];
      setTemplates(templatesData);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setTemplates([]);
        setError(null);
      } else {
        console.error('Failed to fetch training library:', err);
        setError(err?.response?.data?.message || 'Failed to fetch training library');
        setTemplates([]);
      }
    } finally {
      setLoading(false);
    }
  }, [props.team, props.open, trainingLibraryService]);

  useEffect(() => {
    fetchTrainingLibrary();
  }, [fetchTrainingLibrary]);

  const filteredTemplates = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return templates.filter((template) => {
      const searchMatch = !query || (template.title || '').toLowerCase().includes(query);
      const sportMatch = !selectedSport || template.sport === selectedSport;
      const sportTypeMatch = !selectedSportType || template.sport_type === selectedSportType;
      const focusMatch = !selectedFocus || template.focus === selectedFocus;
      return searchMatch && sportMatch && sportTypeMatch && focusMatch;
    });
  }, [templates, searchQuery, selectedSport, selectedSportType, selectedFocus]);

  const filterOptions = useMemo(() => {
    let availableTemplates = templates;

    if (selectedSport) {
      availableTemplates = availableTemplates.filter((t) => t.sport === selectedSport);
    }

    const sports = Array.from(new Set(templates.map((t) => t.sport))).filter(Boolean);
    const sportTypes = Array.from(new Set(availableTemplates.map((t) => t.sport_type))).filter(Boolean);

    if (selectedSportType) {
      availableTemplates = availableTemplates.filter((t) => t.sport_type === selectedSportType);
    }

    const focuses = Array.from(new Set(availableTemplates.map((t) => t.focus))).filter(Boolean);

    return { sports, sportTypes, focuses };
  }, [templates, selectedSport, selectedSportType, selectedFocus]);

  useEffect(() => {
    if (selectedSport) {
      const availableSportTypes = Array.from(
        new Set(templates.filter((t) => t.sport === selectedSport).map((t) => t.sport_type))
      ).filter(Boolean);
      if (selectedSportType && !availableSportTypes.includes(selectedSportType)) {
        setSelectedSportType('');
      }
    }
  }, [templates, selectedSport, selectedSportType]);

  useEffect(() => {
    let available = templates;
    if (selectedSport) {
      available = available.filter((t) => t.sport === selectedSport);
    }
    if (selectedSportType) {
      available = available.filter((t) => t.sport_type === selectedSportType);
    }
    const availableFocuses = Array.from(new Set(available.map((t) => t.focus))).filter(Boolean);
    if (selectedFocus && !availableFocuses.includes(selectedFocus)) {
      setSelectedFocus('');
    }
  }, [templates, selectedSport, selectedSportType, selectedFocus]);

  const handleDuplicate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setDuplicateModalOpen(true);
  };

  const handleCloseDuplicate = () => {
    setDuplicateModalOpen(false);
    setSelectedTemplateId(undefined);
  };

  const handleDuplicateRefresh = () => {
    props.handleRefresh?.();
    handleCloseDuplicate();
    props.handleClose();
  };

  const handleEdit = (templateId: string) => {
    setEditTemplateId(templateId);
  };

  const handleDelete = (templateId: string) => {
    setTemplateToDelete(templateId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!templateToDelete) return;

    const teamId = props.team?.teamId || props.team?.id;
    if (!teamId) return;

    setDeletingTemplate(true);

    trainingLibraryService
      .deleteTrainingTemplate(teamId, templateToDelete)
      .then(() => {
        setDeletingTemplate(false);
        setDeleteDialogOpen(false);
        setTemplateToDelete(null);
        showSnackbar('Successfully deleted training template', 'success');
        fetchTrainingLibrary();
      })
      .catch(() => {
        setDeletingTemplate(false);
        showSnackbar('There was an error while deleting this training template. Please try again later', 'error');
      });
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };

  const handleItemClick = (templateId: string) => {
    setExpandedItemId(expandedItemId === templateId ? null : templateId);
  };

  const filterSelectSx = (selected: boolean) => ({
    borderRadius: '10px',
    height: '44px',
    fontSize: '13px',
    fontWeight: 500,
    backgroundColor: '#fff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: selected ? palette.primary.main : palette.divider,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: palette.primary.main,
    },
    '& .MuiSelect-select': {
      color: selected ? palette.primary.main : palette.text.primary,
      paddingRight: '28px !important',
    },
    '& .MuiSelect-icon': {
      color: selected ? palette.primary.main : palette.text.secondary,
    },
  });

  return (
    <>
      <COETrainingDraftModal
        open={props.open}
        onClose={props.handleClose}
        title="Training Library"
        subtitle="Pick a saved session to add to your plan"
        gradientHeader
        headerIcon={<LibraryBooksOutlinedIcon sx={{ fontSize: 20, color: '#fff' }} />}
        actions={[]}
        hideFooter
      >
        <div className="training-library-container">
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '24px' }}>
              <COESkeletonComponent />
            </div>
          ) : error ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '24px' }}>
              <Typography variant="body1" style={{ color: palette.error.dark }}>
                {error}
              </Typography>
            </div>
          ) : templates.length === 0 ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '24px',
                textAlign: 'center',
              }}
            >
              <Typography variant="body1" style={{ color: palette.text.secondary }}>
                Create & save workouts to your Training Library
              </Typography>
            </div>
          ) : (
            <>
              <div className="training-library-search-row">
                <TextField
                  className="training-library-search"
                  placeholder="Search trainings..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: palette.text.disabled }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" variant="outlined" sx={{ minWidth: 100 }}>
                  <Select
                    value={selectedSport}
                    onChange={(e) => setSelectedSport(e.target.value)}
                    displayEmpty
                    IconComponent={KeyboardArrowDownIcon}
                    className="training-library-filter-chip"
                    sx={filterSelectSx(Boolean(selectedSport))}
                  >
                    <MenuItem value="">Sport</MenuItem>
                    {filterOptions.sports.map((sport) => (
                      <MenuItem key={sport} value={sport} style={{ fontSize: '14px' }}>
                        {sport}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" variant="outlined" sx={{ minWidth: 100 }}>
                  <Select
                    value={selectedSportType}
                    onChange={(e) => setSelectedSportType(e.target.value)}
                    displayEmpty
                    IconComponent={KeyboardArrowDownIcon}
                    className="training-library-filter-chip"
                    sx={filterSelectSx(Boolean(selectedSportType))}
                  >
                    <MenuItem value="">Type</MenuItem>
                    {filterOptions.sportTypes.map((sport_type) => (
                      <MenuItem key={sport_type} value={sport_type} style={{ fontSize: '14px' }}>
                        {sport_type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" variant="outlined" sx={{ minWidth: 100 }}>
                  <Select
                    value={selectedFocus}
                    onChange={(e) => setSelectedFocus(e.target.value)}
                    displayEmpty
                    IconComponent={KeyboardArrowDownIcon}
                    className="training-library-filter-chip"
                    sx={filterSelectSx(Boolean(selectedFocus))}
                  >
                    <MenuItem value="">Focus</MenuItem>
                    {filterOptions.focuses.map((focus) => (
                      <MenuItem key={focus} value={focus} style={{ fontSize: '14px' }}>
                        {focus}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <Typography className="training-library-section-label">Most Used Trainings</Typography>

              {filteredTemplates.length === 0 ? (
                <Typography variant="body2" style={{ color: palette.text.secondary, textAlign: 'center', padding: 24 }}>
                  No trainings match your search or filters
                </Typography>
              ) : (
                <Grid container spacing={2} className="training-library-grid">
                  {filteredTemplates.map((template) => {
                    const headerActions: PopoverAction[] = [
                      {
                        title: 'Edit',
                        color: 'info',
                        action: () => handleEdit(template.id),
                      },
                      {
                        title: 'Delete',
                        color: 'error',
                        action: () => handleDelete(template.id),
                      },
                    ];

                    return (
                      <Grid item xs={6} key={template.id}>
                        <COETrainingTemplateCard
                          template={template}
                          expanded={expandedItemId === template.id}
                          onClick={() => handleItemClick(template.id)}
                          actionType="add"
                          onActionClick={(e) => {
                            e.stopPropagation();
                            handleDuplicate(template.id);
                          }}
                          showExpandedDetails={true}
                          headerActions={headerActions}
                          showAddToPlanButton
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </>
          )}
        </div>
      </COETrainingDraftModal>

      {props.week && (
        <CreateTrainingDraftComponent
          open={duplicateModalOpen}
          handleClose={handleCloseDuplicate}
          handleRefresh={handleDuplicateRefresh}
          team={props.team}
          week={props.week}
          type="template-duplicate"
          trainingId={selectedTemplateId}
          group={props.group}
          defaultDate={props.selectedDate}
        />
      )}

      <EditTrainingComponent
        open={editTemplateId !== null}
        handleClose={() => setEditTemplateId(null)}
        handleRefresh={fetchTrainingLibrary}
        team={props.team}
        week={props.week}
        type="template"
        templateId={editTemplateId || undefined}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        aria-labelledby="delete-template-dialog-title"
        aria-describedby="delete-template-dialog-description"
      >
        <DialogTitle id="delete-template-dialog-title">Delete Training Template</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-template-dialog-description">
            Are you sure you want to delete this training template? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} disabled={deletingTemplate}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={deletingTemplate}>
            {deletingTemplate ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default COETrainingLibraryComponent;
